import { useCallback, useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { v4 as uuid } from "uuid";
import { searchApi } from "common/api/search";
import { getEntityByBehavior } from "common/entities";
import { CancellablePromise } from "common/types/promises";
import { Query } from "common/query/types";
import {
  buildCriticalPartsQuery,
  buildExclusionFilters,
  getPartsWithLocations,
  OPEN_STATUS_ID,
} from "common/record/form/content/related/parts-to-be-counted/functions";
import { Context } from "common/types/context";
import { Properties } from "common/types/records";
import { LoadingButton } from "common/widgets/loading-button";
import { LoadingIcon } from "common/widgets/loading-icon";
import { Modal } from "common/widgets/modal";
import { ValueProps } from "common/with-value-for";
import { RelatedValue } from "../types";

interface PartsToBeCountedCustomFormProps extends ValueProps<RelatedValue> {
  context: Context;
}

export const PartsToBeCountedCustomForm = ({
  context,
  onChange,
  value,
}: PartsToBeCountedCustomFormProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validPartsWithLocations, setValidPartsWithLocations] = useState([]);
  const totalCriticalPartsCount = validPartsWithLocations.length;

  const fetchCriticalParts = async (
    context: Context,
    entityName: string,
    query: Query,
  ): Promise<Properties[]> => {
    return searchApi(context.apiCall).runQuery({
      entity: entityName,
      query,
    }) as CancellablePromise<Properties[]>;
  };

  const loadRecords = useCallback(async () => {
    setIsLoading(true);

    try {
      const entityName = getEntityByBehavior("Part", context.entities).name;
      const exclusionFilters = buildExclusionFilters(value);
      const query = buildCriticalPartsQuery(
        context,
        entityName,
        exclusionFilters,
      );

      const criticalParts = await fetchCriticalParts(
        context,
        entityName,
        query,
      );

      const partsWithLocations = await getPartsWithLocations(
        criticalParts,
        context,
        value,
      );

      unstable_batchedUpdates(() => {
        setValidPartsWithLocations(partsWithLocations);
        setIsLoading(false);
      });
    } catch (error) {
      setIsLoading(false);
    }
  }, [context, value]);

  const openCriticalPartsListModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const closeCriticalPartsListModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleListGeneration = useCallback(() => {
    setIsLoading(true);
    setIsModalOpen(false);

    try {
      const partsToBeCounted = validPartsWithLocations.map((part) => ({
        properties: {
          statusId: OPEN_STATUS_ID,
          partId: part.id,
          partLocationId: part.defaultLocationId,
          tempId: uuid(),
        },
        isNew: true,
        related: {},
      }));

      const existingFormParts = value.related?.form?.PartsToBeCounted || [];

      const allPartsToBeCounted = [...existingFormParts, ...partsToBeCounted];

      const recordsRelated = {
        isDirty: true,
        form: {
          ...value.related.form,
          PartsToBeCounted: allPartsToBeCounted,
        },
        partialForm: value.related.partialForm || {},
      };

      onChange && onChange({ ...value, related: recordsRelated });
      setValidPartsWithLocations([]);
    } finally {
      setIsLoading(false);
    }
  }, [validPartsWithLocations, value, onChange]);

  const generatePartsListModalContent = useCallback(() => {
    if (isLoading) return <LoadingIcon />;

    return (
      <div className="text-center">
        <h4>
          {_(
            "Number of parts to be added to the cycle count session: {PARTS_COUNT}",
          ).replace("{PARTS_COUNT}", totalCriticalPartsCount.toString())}
        </h4>
      </div>
    );
  }, [isLoading, totalCriticalPartsCount]);

  const isButtonDisabled = totalCriticalPartsCount === 0;

  return (
    <div className="x-procedures-custom">
      <LoadingButton
        loadingText={_("Generate Critical Parts List")}
        idleText={_("Generate Critical Parts List")}
        loading={isLoading}
        onClick={openCriticalPartsListModal}
        disabled={isLoading || isButtonDisabled}
        type="action"
        size="large"
      />

      {isModalOpen && (
        <Modal
          content={generatePartsListModalContent()}
          className="x-sandbox-reset-modal"
          size="medium"
          title={_("Generate part list")}
          okLabel={_("Generate")}
          allowFullscreen={false}
          onOk={handleListGeneration}
          onCancel={closeCriticalPartsListModal}
        />
      )}
    </div>
  );
};
